import type { User } from '@aliza/shared';
import {
  authenticateEmail,
  authenticateGoogle,
  authenticateApple,
  authenticateCustom,
  authenticateDevice,
  refreshSession as nakamaRefreshSession,
  logout as nakamaLogout,
  clearSession,
  restoreSession,
  setSession,
  getAccount,
} from '@aliza/nakama-client';
import type { Session } from '@aliza/nakama-client';
import type { AuthSession, LoginCredentials, RegisterCredentials, OAuthProvider } from '../types/auth';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapNakamaUser(account: any): User {
  const u = account.user ?? account;
  return {
    id: u.id ?? u.user_id ?? '',
    username: u.username ?? '',
    displayName: u.display_name ?? u.displayName ?? '',
    avatarUrl: u.avatar_url ?? u.avatarUrl ?? '',
    langTag: u.lang_tag ?? u.langTag ?? '',
    online: u.online ?? false,
    createTime: u.create_time ?? u.createTime ?? '',
    updateTime: u.update_time ?? u.updateTime ?? '',
    metadata: u.metadata ?? {},
  };
}

async function sessionToAuthSession(session: Session): Promise<AuthSession> {
  const account = await getAccount(session);
  setSession(session);
  return {
    token: session.token,
    refreshToken: session.refresh_token,
    expiresAt: session.expires_at ?? 0,
    user: mapNakamaUser(account),
  };
}

export async function login(credentials: LoginCredentials): Promise<AuthSession> {
  const session = await authenticateEmail(credentials.email, credentials.password);
  return sessionToAuthSession(session);
}

export async function register(credentials: RegisterCredentials): Promise<AuthSession> {
  const session = await authenticateEmail(
    credentials.email,
    credentials.password,
    true,
    credentials.username,
  );
  return sessionToAuthSession(session);
}

export async function loginWithOAuth(
  provider: OAuthProvider,
  token: string,
): Promise<AuthSession> {
  let session: Session;
  switch (provider) {
    case 'google':
      session = await authenticateGoogle(token, true);
      break;
    case 'apple':
      session = await authenticateApple(token, true);
      break;
    case 'custom':
      session = await authenticateCustom(token, true);
      break;
  }
  return sessionToAuthSession(session);
}

export async function logout(): Promise<void> {
  stopAutoRefresh();
  const stored = restoreSession();
  if (stored) {
    try {
      await nakamaLogout(stored);
    } catch {
      // Server rejection is fine — token was already expired/invalid
    }
  }
  clearSession();
}

export async function refreshCurrentSession(): Promise<AuthSession> {
  const stored = restoreSession();
  if (!stored) {
    throw new Error('No session to refresh');
  }
  const newSession = await nakamaRefreshSession(stored);
  return sessionToAuthSession(newSession);
}

export function getStoredSession(): AuthSession | null {
  const session = restoreSession();
  if (!session) return null;
  return {
    token: session.token,
    refreshToken: session.refresh_token,
    expiresAt: session.expires_at ?? 0,
    user: {
      id: session.user_id ?? '',
      username: session.username ?? '',
      displayName: '',
      avatarUrl: '',
      langTag: '',
      online: false,
      createTime: '',
      updateTime: '',
      metadata: {},
    },
  };
}

export function clearStoredSession(): void {
  clearSession();
}

// --- Background Token Refresh ---
const REFRESH_MARGIN_MS = 5 * 60 * 1000; // 5 minutes before expiry
let refreshTimer: ReturnType<typeof setInterval> | null = null;

/**
 * Start a background timer that checks token expiry and refreshes proactively.
 * Checks every 60 seconds, refreshes when within 5 minutes of expiry.
 */
export function startAutoRefresh(): void {
  stopAutoRefresh();
  refreshTimer = setInterval(async () => {
    try {
      const stored = restoreSession();
      if (!stored) return;

      const expiresAt = (stored.expires_at ?? 0) * 1000; // convert to ms
      const now = Date.now();
      const remaining = expiresAt - now;

      if (remaining > 0 && remaining <= REFRESH_MARGIN_MS) {
        const newSession = await nakamaRefreshSession(stored);
        setSession(newSession);
      }
    } catch {
      // Refresh failed — will retry next interval
    }
  }, 60_000);
}

/** Stop the background refresh timer (called on logout). */
export function stopAutoRefresh(): void {
  if (refreshTimer) {
    clearInterval(refreshTimer);
    refreshTimer = null;
  }
}

const GUEST_DEVICE_ID_KEY = 'aliza_guest_device_id';

export async function loginAsGuest(): Promise<AuthSession> {
  let deviceId = localStorage.getItem(GUEST_DEVICE_ID_KEY);
  if (!deviceId) {
    deviceId = crypto.randomUUID();
    localStorage.setItem(GUEST_DEVICE_ID_KEY, deviceId);
  }
  const session = await authenticateDevice(deviceId, true);
  return sessionToAuthSession(session);
}
